import axios from 'axios';
import _ from 'lodash';

class API {
  /**
   * Class contructor
   * @param  {String} baseURL       The base url for API calls
   */
  constructor(baseURL) {
    this.baseURL = baseURL;
  }

  formatParams(params) {
    if (!params || params.length == 0) {
      return '';
    }

    let formattedParams = '?';
    let paramsProperties = [];

    for (let key in params) {
      paramsProperties.push(`${key}=${String(params[key])}`);
    }

    paramsProperties.forEach((param, index) => {
      formattedParams += param;
      if (index < paramsProperties.length - 1) {
        formattedParams += '&';
      }
    });

    return formattedParams;
  }

  /**
   * Send a GET request
   * @param  {String} endpoint      Endpoint to request
   * @param  { Object} params       Params to request
   * @return {Promise}              Request promise
   */
  get(endpoint, params) {
    const baseURL = this.baseURL;
    const formattedParams = this.formatParams(params);
    let axiosConfig;

    if (process.env.REACT_APP_REQ_AUTH) {
      axiosConfig = {
        auth: {
          username: process.env.REACT_APP_AUTH_USER,
          password: process.env.REACT_APP_AUTH_PASSWORD,
        },
        // withCredentials: true,
      };
    }

    return new Promise((resolve, reject) => {
      axios
        .get(`${baseURL}${endpoint}${formattedParams}`, axiosConfig)
        .then(response => {
          return response.data;
        })
        .then(resolve)
        .catch(reject);
    });
  }

  getTvGuide(nid = '', start = '') {
    return this.get('/tv-guide', {
      nid: nid,
      start: start,
    }).then(response => {
      return response.data.programs;
    });
  }

  getTvGuidePrograms(nid = '', start = '') {
    return this.get('/tv-guide', {
      nid: nid,
      start: start,
    }).then(response => {
      const programs = [];

      response.data.programs.forEach(c => {
        c.events.forEach(p => {
          programs.push({
            ...p,
            service_id: c.service_id,
          });
        });
      });

      return programs;
    });
  }

  getWhatsOn(nid = '') {
    return this.get('/tv-guide', {
      nid: nid,
      now_next: true,
    }).then(response => {
      let programs = [];

      response.data.programs.forEach(p => {
        p.events.forEach(e => {
          programs.push({
            ...e,
            service_id: p.service_id,
          });
        });
      });

      return programs;
    });
  }

  getChannels(nid = '') {
    if (window.sessionStorage.getItem(`channels:${nid}`)) {
      return Promise.resolve(
        JSON.parse(window.sessionStorage.getItem(`channels:${nid}`)),
      ).then(channels => channels);
    } else {
      return this.get('/channel-list', {
        nid: nid,
      }).then(response => {
        const channels = response.data.services.map(c => {
          return { ...c, programs: [] };
        });
        window.sessionStorage.setItem(
          `channels:${nid}`,
          JSON.stringify(channels),
        );
        return channels;
      });
    }
  }

  getProgramDetailsSimple(pid = '', sid = '') {
    return this.get('/program', {
      pid: pid,
      sid: sid,
    }).then(response => {
      return response.data.programs[0];
    });
  }

  getProgramDetails(
    sid = '',
    nid = '',
    pid = '',
    start_time = '',
    duration = '',
  ) {
    return this.get('/program', {
      sid: sid,
      nid: nid,
      pid: pid,
      start_time: start_time,
      duration: duration,
    }).then(response => {
      return response.data.programs[0];
    });
  }

  getProgramMoreEpisodes(nid = '', id = '') {
    return this.get('/more-episodes', {
      nid: nid,
      pid: id,
    }).then(response => {
      return response.data.programs;
    });
  }

  getRegionsIds() {
    return this.get('/get-region-network-ids');
  }

  getNetworkId(locationParams) {
    return this.get('/get-network-id', locationParams).then(response => {
      return response.data.network_id;
    });
  }

  getProgramRecommendations(gid = '', nid = '') {
    const recommendationsStorageId =
      'programRecommendations:' + gid + ':' + nid;
    if (window.sessionStorage.getItem(recommendationsStorageId)) {
      return new Promise((resolve, reject) => {
        resolve(
          JSON.parse(window.sessionStorage.getItem(recommendationsStorageId)),
        );
      });
    } else {
      return this.get('/program/recommendations', {
        nid: nid,
        group_id: gid,
      }).then(response => {
        const programs = response.data.programs;
        window.sessionStorage.setItem(
          recommendationsStorageId,
          JSON.stringify(programs),
        );
        return programs;
      });
    }
  }

  getProgramSearch(q = '', nid = '') {
    return this.get('/programs/search', {
      q: q,
      nid: nid,
    }).then(response => {
      return response.data.programs;
    });
  }

  getMobileOwningServices() {
    // Check to see if we already have the services.
    if (window.sessionStorage.getItem('contentOwningServices')) {
      return JSON.parse(window.sessionStorage.getItem('contentOwningServices'));
    } else {
      return this.get('/content-owning-services').then(response => {
        const services = response.data.services;
        window.sessionStorage.setItem(
          'contentOwningServices',
          JSON.stringify(services),
        );
        return services;
      });
    }
  }

  getTriage(target) {
    return this.get(`/triage/${target}`).then(res => {
      return res.data;
    });
  }

  getStatuscheck(channels, location) {
    let params = '?';
    let axiosConfig;

    if (location.postcode) {
      params += 'postcode=' + location.postcode;
      params += '&address=' + (location.address || null);
    } else if (location.lat && location.lon) {
      params += 'lat=' + location.lat;
      params += '&lon=' + location.lon;
    } else {
      console.error('API.getStatuscheck. No postcode or lat/long provided.');
      return;
    }
    channels.forEach(c => {
      params += '&channels[]=' + c;
    });

    if (process.env.REACT_APP_REQ_AUTH) {
      axiosConfig = {
        auth: {
          username: process.env.REACT_APP_AUTH_USER,
          password: process.env.REACT_APP_AUTH_PASSWORD,
        },
      };
    }

    return new Promise((resolve, reject) => {
      axios
        .get(`${this.baseURL}/status-checker${params}`, axiosConfig)
        .then(response => {
          return response.data.data;
        })
        .then(resolve)
        .catch(reject);
    });
  }

  getBoxSets(gid = '', nid = '') {
    return this.get('/box-sets/content', {
      nid: nid,
      group_id: gid,
    }).then(response => {
      return response.data.programs;
    });
  }
}

/**
 * @return {API} Return API singleton object
 */
export default (() => {
  let instance;

  if (!instance) {
    instance = new API(process.env.API_URL);
  }

  return instance;
})();
